import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, TextButton } from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import {
  BALANCE_GENERATION_LAYOUT_STORAGE_KEY,
  useGridCardLayout,
} from '~/hooks/useGridCardLayout'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { AREA_CODE_OPTIONS } from '~/models/area'
import {
  BgGrid,
  selectVisibleBgs,
} from '~/pages/BalanceGeneration/components/BgGrid'
import { FilterCard } from '~/pages/BalanceGeneration/components/FilterCard'
import { useFilterState } from '~/pages/BalanceGeneration/hooks/useFilterState'
import { useGenerationDashboard } from '~/pages/BalanceGeneration/hooks/useGenerationDashboard'
import { convertGenerationDashboardResponse } from '~/pages/BalanceGeneration/models/convertGenerationResponse'

// doji-grid-layout: design hatsu-data.js の GridStack 設定 (column:12,
// cellHeight:70, margin:6) と揃える。
//
// 高さの根拠 (BgChart の実コードから積んだ概算):
//   ヘッダー行: padding 4+4=8px + テキスト(sm=14px)行 ≒ 17px + border-bottom 1px
//     → 計 26px
//   Stats 行: padding 4+4=8px + border-bottom 1px。内容は sm=14px 太字を含む
//     3 span (flexWrap、gap 10px)。カード幅が 1/3 では折り返して 2 行化しうる
//     ため最悪ケース (2 行 + 行間 gap) で見積もる: 8+17+10+17+1 = 53px
//   軸ラベル行: padding-top 1px + lineHeight:1 (xs=10px) = 11px
//   チャート本体: minHeight 160px (コード内の明示的な床)
//   root border: 上下 1px ずつ = 2px
//   → 概算合計 ≒ 26+53+11+160+2 = 252px (2 行折返し込みの最悪ケース)
// MIN h=4 (298px = 4*70+3*6) はこの ~252px を上回る安全マージンを確保する
// (旧実装の h=3=222px はこの概算を下回り、2 行折返し時にクリップしうる)。
// 既定サイズは変更前の固定 320px を下回らないことを目安にする。h=4=298px は
// 320px 未達のため採用せず、h=5 (374px = 5*70+4*6) を既定にする。
// 幅方向 (minW=3 列) は YAxis width=52px と Stats 行のテキスト量に対して
// 十分な余裕がある (RegionChart の Y_AXIS_WIDTH=84px より狭いぶん、なお安全)
// ため据え置く。
const DEFAULT_CARD_SIZE = { w: 4, h: 5 }
const MIN_CARD_SIZE = { w: 3, h: 4 }
// export = R6: F5 test (index.test.tsx) がレイアウトリセットの localStorage
// key を参照するため、生文字列で二重定義しない。キー定数自体の SoT は
// `~/hooks/useGridCardLayout` 側 (= ログアウト時の破棄 `clearGridCardLayouts`
// と同じ定数を共有する必要があるため)。
export const LAYOUT_STORAGE_KEY = BALANCE_GENERATION_LAYOUT_STORAGE_KEY

export const BalanceGenerationPage = (): React.ReactElement => {
  const { t } = useTranslation()

  // issue #7535 / conventions.md「業務エリアフィルタの原則」: エリアセレクターの
  // 選択肢 = 対象エリア (AREA_CODE_OPTIONS、全 10 エリア) ∩ 業務エリア設定。
  // 取得・フォールバック・エラー公開のポリシーは共有 hook 側の JSDoc 参照
  // (`~/hooks/useBusinessAreaFilter`。 需要側 `BalanceDemand` と同じ配線)。
  const { selectableAreaCodes, isBusinessAreasError } =
    useBusinessAreaFilter(AREA_CODE_OPTIONS)

  const filter = useFilterState(selectableAreaCodes)
  const { data, isRegionLoading, isError } = useGenerationDashboard(
    filter.displayDate,
  )

  const dashboard =
    data != null ? convertGenerationDashboardResponse(data) : undefined

  /**
   * バナーは取得失敗のたびに出す (AC-BDH-013 は初回に限定していない。需要側と同じ)。
   * 業務エリア取得の失敗もここに合流させる (issue #7535 repair、需要側と同じ理由)。
   */
  const showLoadError = isError || isBusinessAreasError

  /** ローディングを止めるのは初回取得が失敗した時だけ (需要側と同じ)。 */
  const initialLoadFailed = data === undefined && isError

  // workflow-back-bar-readonly AC-10/AC-14: caseId は URL クエリパラメータ
  // `?workflowId=` から取得し、詳細リンク (BgChart) へ
  // 引き継ぐ。
  const { caseId } = useCurrentWorkflowCase()

  // doji-grid-layout: レイアウト hook をページ側で持つ (= BgGrid が isLoading
  // 中は unmount されているため、hook を Grid 側に置くと「読み込み中にレイアウト
  // リセットを押しても、次のマウント時に効果が握り潰される」バグが起きる。hook
  // をページに常駐させ、resetLayout を直接 FilterCard へ渡す)。
  const visibleBgs = useMemo(
    () => selectVisibleBgs(dashboard?.bgs ?? [], filter.visibleAreaCodes),
    [dashboard, filter.visibleAreaCodes],
  )
  const cardIds = useMemo(
    () => visibleBgs.map((bg) => bg.config.code),
    [visibleBgs],
  )
  const { layout, onLayoutSettled, resetLayout } = useGridCardLayout({
    storageKey: LAYOUT_STORAGE_KEY,
    cardIds,
    defaultSize: DEFAULT_CARD_SIZE,
    minSize: MIN_CARD_SIZE,
  })

  const breadcrumbItems = [
    { label: t('common:nav.balance') },
    { label: t('page:balanceGeneration.title'), bold: true },
  ]

  return (
    <WorkflowPageLayout breadcrumbItems={breadcrumbItems}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <FilterCard filter={filter} />

        {/* doji-grid-layout AC-BDH-008:「レイアウトリセット」。 2026-09-07 の
            デザイナー判断で、フィルタカードの中 (8080 の `#layout-reset-btn` の
            位置) から**カードの外・グラフの上の右端**へ移した (需要側と同じ)。 */}
        {/* 「積み上げ順・色」はフィルタカードの「発電形式内訳」行の右端へ移した (#7058)。 */}
        <Flex justifyContent="flex-end" gap={3} marginBottom="4px">
          <TextButton onClick={resetLayout} size="sm">
            {t('page:balanceGeneration.filter.layoutReset')}
          </TextButton>
        </Flex>

        {/* 取得失敗のバナー。 **レイアウトリセットの下 = 本来グラフが出る領域**に置く
            (2026-09-09 デザイナー指定「alert が指しているデータはグラフであるため」。
            需要側と同じ)。

            [spec 差異] AC-BDH-013 の文言は「ダッシュボード**上部**にエラーバナー」 だが、
            上部 (フィルタ直下) だと**何が取れなかったのか**が結び付かない。 バナーが
            指しているのは BG カード群のデータなので、 その器の中に出す。 spec 側の
            追随は #7064 で扱う。

            AC は初回取得に限定していないので、 背景 refetch の失敗でもバナーを出す。
            その場合は**カードを残したままバナーを上に足す**。

            初回取得に失敗した時は**ローディングを出さない** (以前は error でも
            永久にローディング表示だった)。 */}
        {showLoadError && (
          <Box marginBottom="4px">
            <CompactAlert
              status="error"
              title={t('page:balanceGeneration.loadError')}
            />
          </Box>
        )}

        {initialLoadFailed ? null : (
          <RegionLoading
            flexDirection="column"
            flex="1"
            display="flex"
            minHeight={dashboard == null ? '200px' : 0}
            isLoading={isRegionLoading}
          >
            {dashboard == null ? null : (
              <BgGrid
                bgs={dashboard.bgs}
                caseId={caseId}
                displayDate={filter.displayDate}
                gcKoma={dashboard.gcKoma}
                layout={layout}
                onLayoutSettled={onLayoutSettled}
                seriesVisibility={filter.seriesVisibility}
                visibleAreaCodes={filter.visibleAreaCodes}
              />
            )}
          </RegionLoading>
        )}
      </div>
    </WorkflowPageLayout>
  )
}
